import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import {
  Usuario,
  CategoriaProfissional,
  RegistroProfissional,
} from "../../../types";
import { auth, db } from "../../../lib/firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { limparSessao } from "../../prescricoes/services/cofreSessaoAssinatura";

interface AuthContextData {
  usuarioLogado: Usuario | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  erroLogin: string;
  limparErroLogin: () => void;
  login: (
    email: string,
    senha: string
  ) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  setUsuarioLogado: (usuario: Usuario | null) => void;
  cadastrarUsuario: (
    dados: CadastroUsuario
  ) => Promise<{ success: boolean; message: string }>;
  trocarSenha: (senhaAtual: string, novaSenha: string) => Promise<void>;
  recuperarSenha: (
    email: string
  ) => Promise<{ success: boolean; message: string }>;
}

// ========================================
// INTERFACE ATUALIZADA DE CADASTRO
// ========================================
interface CadastroUsuario {
  email: string;
  senha: string;
  nome: string;
  tipo: "administrador" | "usuario" | "convidado";

  // ⭐ NOVOS CAMPOS
  categoria: CategoriaProfissional;
  registroProfissional?: RegistroProfissional;

  // CAMPOS LEGADOS (manter compatibilidade)
  crm?: string;
  especialidade?: string;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuarioLogado, setUsuarioLogado] = useState<Usuario | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [erroLogin, setErroLogin] = useState("");
  // Marca se o usuário já esteve logado nesta vida da página — para limpar o
  // cofre de assinatura SÓ numa transição real logado→deslogado (logout/
  // expiração/navegação externa), nunca no `null` inicial do boot ou da abertura
  // de janela nova (que apagaria um cofre válido sendo restaurado).
  const estavaLogadoRef = useRef(false);

  const limparErroLogin = () => setErroLogin("");

  // ============================================
  // 📥 CARREGAR DADOS DO USUÁRIO DO FIRESTORE
  // ============================================
  const carregarUsuario = async (uid: string) => {
    console.log("🔍 Carregando usuário:", uid);

    try {
      const userDoc = await getDoc(doc(db, "usuarios", uid));

      if (userDoc.exists()) {
        const data = userDoc.data();

        const usuario: Usuario = {
          id: uid,
          nome: data.nome,
          email: data.email,
          tipo: data.tipo,

          // ⭐ NOVOS CAMPOS - CATEGORIA PROFISSIONAL
          categoria: data.categoria || "medico", // Default para médico (compatibilidade)
          registroProfissional: data.registroProfissional,

          // CAMPOS LEGADOS (compatibilidade)
          crm: data.crm || undefined,
          especialidade: data.especialidade || undefined,

          // 🏥 DADOS DO SOLICITANTE (B211 — formulários auxiliares SUS)
          dadosSolicitante: data.dadosSolicitante || undefined,

          ativo: data.ativo ?? true,
          // 🔒 B103 — troca de senha obrigatória no 1º acesso
          precisaTrocarSenha: data.precisaTrocarSenha ?? false,
          criadoEm:
            data.criadoEm?.toDate?.()?.toISOString?.() ||
            new Date().toISOString(),
          atualizadoEm:
            data.atualizadoEm?.toDate?.()?.toISOString?.() ||
            new Date().toISOString(),
        };

        // B318 (AI_143, M5): sem nome no console — id + categoria bastam
        // para depurar (LGPD).
        console.log(
          "✅ Usuário carregado:",
          usuario.id,
          "- Categoria:",
          usuario.categoria
        );
        setUsuarioLogado(usuario);

        return usuario;
      } else {
        console.warn("⚠️ Usuário não encontrado no Firestore");
        return null;
      }
    } catch (error) {
      console.error("❌ Erro ao carregar usuário:", error);
      return null;
    }
  };

  // ============================================
  // 🔍 LISTENER DE AUTENTICAÇÃO
  // ============================================
  useEffect(() => {
    console.log("🚀 Inicializando Firebase Auth...");

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log(
        "🔔 Auth state changed:",
        firebaseUser ? "Logado" : "Deslogado"
      );

      setFirebaseUser(firebaseUser);

      if (firebaseUser) {
        estavaLogadoRef.current = true;
        await carregarUsuario(firebaseUser.uid);
      } else {
        setUsuarioLogado(null);
        // 🔐 Segurança: a sessão de assinatura morre junto com a sessão do app
        // (logout, logout-por-navegação-externa, expiração do token). Só limpa
        // numa transição REAL logado→deslogado — nunca no `null` inicial do boot
        // ou da abertura de janela nova, para não apagar um cofre válido que
        // está sendo restaurado (senão a janela nova voltaria a pedir QR).
        if (estavaLogadoRef.current) {
          estavaLogadoRef.current = false;
          limparSessao();
        }
      }

      setLoading(false);
    });

    // Cleanup
    return () => unsubscribe();
  }, []);

  // ============================================
  // 🔒 SEGURANÇA: Deslogar ao sair da página
  // ============================================
  useEffect(() => {
    // === MÉTODO 1: pagehide/pageshow (funciona melhor no Chrome) ===
    const handlePageHide = async (event: PageTransitionEvent) => {
      if (!event.persisted && firebaseUser) {
        console.log("🔒 Segurança: Saindo da página, deslogando...");
        sessionStorage.setItem("hepato_deve_deslogar", "true");
      }
    };

    const handlePageShow = async (event: PageTransitionEvent) => {
      const deveDeslogar = sessionStorage.getItem("hepato_deve_deslogar");
      if (deveDeslogar === "true" || event.persisted) {
        sessionStorage.removeItem("hepato_deve_deslogar");
        if (firebaseUser) {
          console.log("🔒 Segurança: Página restaurada, deslogando...");
          await signOut(auth);
        }
      }
    };

    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);

    // Verifica ao montar se deve deslogar (sessionStorage)
    const deveDeslogar = sessionStorage.getItem("hepato_deve_deslogar");
    if (deveDeslogar === "true") {
      sessionStorage.removeItem("hepato_deve_deslogar");
      if (firebaseUser) {
        signOut(auth);
      }
    }

    // === MÉTODO 2: localStorage com timestamp (funciona melhor no Safari) ===
    // Já está implementado no firebase.ts

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [firebaseUser]);

  // ============================================
  // 🔐 LOGIN
  // ============================================
  const login = async (
    email: string,
    senha: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      console.log("🔐 Tentando login...");

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        senha
      );

      console.log("✅ Autenticação bem-sucedida!");

      // Verifica se está ativo
      const userDoc = await getDoc(
        doc(db, "usuarios", userCredential.user.uid)
      );

      if (!userDoc.exists()) {
        await signOut(auth);
        const msg = "Usuário não encontrado no sistema.";
        setErroLogin(msg);
        return {
          success: false,
          message: msg,
        };
      }

      const userData = userDoc.data();

      if (!userData.ativo) {
        await signOut(auth);
        const msg = "Usuário inativo. Entre em contato com o administrador.";
        setErroLogin(msg);
        return {
          success: false,
          message: msg,
        };
      }

      return {
        success: true,
        message: "Login realizado com sucesso!",
      };
    } catch (error: any) {
      console.error("❌ Erro no login:", error);
      console.error("❌ Código do erro:", error.code);
      let message = "Erro ao fazer login. Tente novamente.";

      // Firebase Auth error codes
      switch (error.code) {
        case "auth/invalid-credential":
        case "auth/wrong-password":
        case "auth/invalid-email":
          message = "Email ou senha incorretos";
          break;
        case "auth/user-not-found":
          message = "Usuário não encontrado";
          break;
        case "auth/too-many-requests":
          message = "Muitas tentativas. Aguarde alguns minutos.";
          break;
        case "auth/user-disabled":
          message = "Usuário desativado. Contate o administrador.";
          break;
        case "auth/network-request-failed":
          message = "Erro de conexão. Verifique sua internet.";
          break;
        default:
          message = `Erro ao fazer login: ${error.code || "desconhecido"}`;
      }

      setErroLogin(message);

      return {
        success: false,
        message,
      };
    }
  };

  // ============================================
  // 🚪 LOGOUT
  // ============================================
  const logout = async () => {
    try {
      console.log("🚪 Fazendo logout...");
      limparSessao(); // 🔐 apaga a sessão de assinatura do navegador
      await signOut(auth);
      setUsuarioLogado(null);
      setFirebaseUser(null);
      console.log("✅ Logout realizado");
    } catch (error) {
      console.error("❌ Erro ao fazer logout:", error);
    }
  };

  // ============================================
  // ✍️ CADASTRAR USUÁRIO (ATUALIZADO)
  // ============================================
  const cadastrarUsuario = async (
    dados: CadastroUsuario
  ): Promise<{ success: boolean; message: string }> => {
    try {
      // B318 (AI_143, M5): categoria basta p/ depurar; registro
      // profissional (CRM/COREN) é dado pessoal — fora do console.
      console.log("📝 Cadastrando novo usuário...");
      console.log("📋 Categoria:", dados.categoria);

      // Criar usuário no Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        dados.email,
        dados.senha
      );

      // ========================================
      // PREPARAR DADOS PARA O FIRESTORE
      // ========================================
      const dadosFirestore: any = {
        nome: dados.nome,
        email: dados.email,
        tipo: dados.tipo,

        // ⭐ NOVOS CAMPOS - CATEGORIA PROFISSIONAL
        categoria: dados.categoria,
        registroProfissional: dados.registroProfissional || null,

        // CAMPOS LEGADOS (compatibilidade - manter por enquanto)
        crm: dados.crm || null,
        especialidade: dados.especialidade || null,

        ativo: true,
        criadoEm: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      };

      // Criar documento no Firestore
      await setDoc(
        doc(db, "usuarios", userCredential.user.uid),
        dadosFirestore
      );

      // B318 (AI_143, M5): NÃO despejar o doc inteiro (nome, e-mail,
      // registro) no console — só a confirmação.
      console.log("✅ Usuário cadastrado com sucesso!");

      return {
        success: true,
        message: "Usuário cadastrado com sucesso!",
      };
    } catch (error: any) {
      console.error("❌ Erro ao cadastrar usuário:", error);

      let message = "Erro ao cadastrar usuário";

      if (error.code === "auth/email-already-in-use") {
        message = "Este email já está em uso";
      } else if (error.code === "auth/weak-password") {
        message = "Senha muito fraca. Use pelo menos 6 caracteres.";
      } else if (error.code === "auth/invalid-email") {
        message = "Email inválido";
      }

      return {
        success: false,
        message,
      };
    }
  };

  // ============================================
  // 🔐 TROCAR SENHA
  // ============================================
  const trocarSenha = async (senhaAtual: string, novaSenha: string) => {
    if (!auth.currentUser) {
      throw new Error("Usuário não autenticado");
    }

    try {
      console.log("🔐 Trocando senha...");

      // 1️⃣ Re-autenticar usuário (necessário para trocar senha)
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email!,
        senhaAtual
      );

      await reauthenticateWithCredential(auth.currentUser, credential);
      console.log("✅ Re-autenticação bem-sucedida");

      // 2️⃣ Atualizar senha
      await updatePassword(auth.currentUser, novaSenha);
      console.log("✅ Senha alterada com sucesso!");

      // 3️⃣ B103 — se era troca obrigatória (1º acesso), limpa a flag no
      // Firestore e no estado local, liberando o acesso ao sistema.
      if (usuarioLogado?.precisaTrocarSenha) {
        await updateDoc(doc(db, "usuarios", auth.currentUser.uid), {
          precisaTrocarSenha: false,
          atualizadoEm: serverTimestamp(),
        });
        setUsuarioLogado({ ...usuarioLogado, precisaTrocarSenha: false });
        console.log("✅ Flag de troca obrigatória limpa (B103)");
      }
    } catch (error: any) {
      console.error("❌ Erro ao trocar senha:", error);
      throw error;
    }
  };

  // ============================================
  // 🔑 RECUPERAR SENHA (ESQUECI MINHA SENHA)
  // ============================================
  const recuperarSenha = async (
    email: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      // B318 (AI_143, M5): e-mail é dado pessoal — fora do console.
      console.log("📧 Enviando email de recuperação...");
      await sendPasswordResetEmail(auth, email);
      console.log("✅ Email de recuperação enviado!");
      return {
        success: true,
        message:
          "Email de recuperação enviado! Verifique sua caixa de entrada e spam.",
      };
    } catch (error: any) {
      console.error("❌ Erro ao enviar email de recuperação:", error);
      let message = "Erro ao enviar email de recuperação.";
      switch (error.code) {
        case "auth/user-not-found":
        case "auth/invalid-email":
          message = "Email não encontrado no sistema.";
          break;
        case "auth/too-many-requests":
          message = "Muitas tentativas. Aguarde alguns minutos.";
          break;
      }
      return { success: false, message };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        usuarioLogado,
        firebaseUser,
        loading,
        erroLogin,
        limparErroLogin,
        login,
        logout,
        setUsuarioLogado,
        cadastrarUsuario,
        trocarSenha,
        recuperarSenha,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
